import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { Heart, Share2, Eye, Trash2 } from 'lucide-react';
import { ToolPageShell } from '../components/tool';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { streamInvoke } from '../lib/safeInvoke';
import { ZODIAC_SIGNS, getSignEmoji, getSignName } from '../lib/astrology';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingActions } from '../components/shared/ReadingActions';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';
import { usePricing } from '../contexts/PricingContext';
import { useAvulsoProduct } from '../hooks/useAvulsoProducts';
import { AvulsoCompareModal } from '../components/shared/AvulsoCompareModal';
import {
  calculateFullSynastry,
  calcInterAspects,
  type FullSynastryData,
  type CompatibilityScores,
} from '../services/synastryCalc';

// ── Helpers ───────────────────────────────────────────────────────────────────

function displayToDate(s: string): Date | null {
  const [d, m, y] = s.split('/');
  if (!y || y.length !== 4) return null;
  const dt = new Date(+y, +m - 1, +d);
  return isNaN(dt.getTime()) ? null : dt;
}

function displayToDb(s: string): string {
  const [d, m, y] = s.split('/');
  if (!y || y.length !== 4) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

function scoreColor(n: number) {
  return n >= 70 ? '#4ade80' : n >= 50 ? '#F5C842' : '#fb923c';
}

function aspectColor(q: string) {
  return q === 'harmonious' ? '#4ade80' : q === 'tense' ? '#fb923c' : '#c084fc';
}

function renderMd(text: string) {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

function signFromDate(d: Date): string {
  const m = d.getMonth() + 1, day = d.getDate();
  if ((m===3&&day>=21)||(m===4&&day<=19)) return 'aries';
  if ((m===4&&day>=20)||(m===5&&day<=20)) return 'taurus';
  if ((m===5&&day>=21)||(m===6&&day<=20)) return 'gemini';
  if ((m===6&&day>=21)||(m===7&&day<=22)) return 'cancer';
  if ((m===7&&day>=23)||(m===8&&day<=22)) return 'leo';
  if ((m===8&&day>=23)||(m===9&&day<=22)) return 'virgo';
  if ((m===9&&day>=23)||(m===10&&day<=22)) return 'libra';
  if ((m===10&&day>=23)||(m===11&&day<=21)) return 'scorpio';
  if ((m===11&&day>=22)||(m===12&&day<=21)) return 'sagittarius';
  if ((m===12&&day>=22)||(m===1&&day<=19)) return 'capricorn';
  if ((m===1&&day>=20)||(m===2&&day<=18)) return 'aquarius';
  return 'pisces';
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface HistoryItem {
  id: string;
  partner_name: string;
  partner_birth_date: string;
  compatibility_score: number;
  relation_type: string | null;
  reading_text: string | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SCORE_CONFIG: Record<keyof CompatibilityScores, { tKey: string; symbol: string; color: string }> = {
  love:          { tKey: 'scoreLove',          symbol: '♀', color: '#ec4899' },
  communication: { tKey: 'scoreCommunication', symbol: '☿', color: '#60a5fa' },
  values:        { tKey: 'scoreValues',        symbol: '♃', color: '#4ade80' },
  longTerm:      { tKey: 'scoreLongTerm',      symbol: '♄', color: '#818cf8' },
  growth:        { tKey: 'scoreGrowth',        symbol: '♃', color: '#c084fc' },
  routine:       { tKey: 'scoreRoutine',       symbol: '☽', color: '#fbbf24' },
  family:        { tKey: 'scoreFamily',        symbol: '☽', color: '#2dd4bf' },
};

const RELATION_LABELS: Record<string, string> = {
  amigo:    'Amizade',
  crush:    'Crush',
  parceiro: 'Parceiro(a) Romântico(a)',
  parente:  'Vínculo Familiar',
  trabalho: 'Colega de Trabalho',
};

const RELATION_BADGE_LABELS: Record<string, string> = {
  amigo:    'Amizade',
  crush:    'Crush',
  parceiro: 'Romântico',
  parente:  'Família',
  trabalho: 'Trabalho',
};

// Análise is 2nd so it's immediately accessible after Score
const MAIN_TAB_KEYS = [
  { key: 'score',       tKey: 'tabScore' },
  { key: 'analise',     tKey: 'tabAnalise' },
  { key: 'aspectos',    tKey: 'tabAspectos' },
  { key: 'casas',       tKey: 'tabCasas' },
  { key: 'composto',    tKey: 'tabComposto' },
  { key: 'davison',     tKey: 'tabDavison' },
  { key: 'transitos',   tKey: 'tabTransitos' },
  { key: 'progressoes', tKey: 'tabProgressoes' },
];

const ASPECT_FILTERS = [
  ['all', 'Todos'], ['conjunction', 'Conjunção'], ['trine', 'Trígono'],
  ['sextile', 'Sextil'], ['square', 'Quadratura'], ['opposition', 'Oposição'],
] as const;

// ── Sub-components ────────────────────────────────────────────────────────────

function ScoreLine({ cfg, value }: { cfg: { tKey: string; symbol: string; color: string }; value: number }) {
  const { t } = useI18n();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
      <span style={{ fontSize: '1rem', width: '1.25rem', textAlign: 'center', flexShrink: 0, color: cfg.color }}>{cfg.symbol}</span>
      <span style={{ width: '8rem', flexShrink: 0, fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{t(`pages.synastry.${cfg.tKey}`)}</span>
      <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: cfg.color, borderRadius: '3px', transition: 'width 1s ease' }} />
      </div>
      <span style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: cfg.color, width: '2.5rem', textAlign: 'right', flexShrink: 0 }}>{value}%</span>
    </div>
  );
}

function PlanetTable({ planets }: { planets: { namePt: string; symbol: string; sign: string; degree: number; house: number; isRetrograde: boolean }[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-caption)' }}>
      <thead>
        <tr style={{ color: 'var(--text-muted)' }}>
          <th style={{ textAlign: 'left',   padding: '0.25rem 0.5rem', fontWeight: 500 }}>Planeta</th>
          <th style={{ textAlign: 'left',   padding: '0.25rem 0.5rem', fontWeight: 500 }}>Signo</th>
          <th style={{ textAlign: 'center', padding: '0.25rem 0.5rem', fontWeight: 500 }}>Grau</th>
          <th style={{ textAlign: 'center', padding: '0.25rem 0.5rem', fontWeight: 500 }}>Casa</th>
        </tr>
      </thead>
      <tbody>
        {planets.map((p, i) => (
          <tr key={i} style={{ borderTop: '1px solid var(--border-default)' }}>
            <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-primary)' }}>{p.symbol} {p.namePt}{p.isRetrograde ? ' ℞' : ''}</td>
            <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)' }}>{p.sign}</td>
            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{p.degree.toFixed(1)}°</td>
            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: 'var(--petrol)' }}>{p.house}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── SynastryResults ───────────────────────────────────────────────────────────

interface ResultsProps {
  synData: FullSynastryData;
  userName: string;
  partnerName: string;
  partnerSign: string;
  relationType: string;
  overallScore: number;
  profile: { sun_sign?: string | null; full_name?: string | null } | null;
  activeTab: string;
  setActiveTab: (t: string) => void;
  aspectFilter: string;
  setAspectFilter: (f: string) => void;
  filteredAspects: ReturnType<typeof calcInterAspects>;
  transitsToComposite: ReturnType<typeof calcInterAspects>;
  aiLoading: boolean;
  aiError: string | null;
  streamingText: string;
  doneText: string;
  analysisSections: { num: number; title: string; content: string }[];
  aiSubTab: number;
  setAiSubTab: (n: number) => void;
  onNewAnalysis: () => void;
}

function SynastryResults({
  synData, userName, partnerName, partnerSign, relationType, overallScore, profile,
  activeTab, setActiveTab, aspectFilter, setAspectFilter,
  filteredAspects, transitsToComposite,
  aiLoading, aiError, streamingText, doneText, analysisSections, aiSubTab, setAiSubTab,
  onNewAnalysis,
}: ResultsProps) {
  const { t } = useI18n();

  return (
    <div className="animate-fade-in">

      {/* ── Partner header card ────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '1.5rem' }}>{getSignEmoji(profile?.sun_sign ?? '')}</span>
          <span style={{ color: 'var(--text-muted)' }}>×</span>
          <span style={{ fontSize: '1.5rem' }}>{getSignEmoji(partnerSign)}</span>
          <div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
              {userName} & {partnerName}
            </p>
            <p style={{ color: 'var(--text-tertiary)', margin: 0, fontSize: 'var(--text-caption)' }}>
              {getSignName(profile?.sun_sign ?? '')} + {partnerSign ? getSignName(partnerSign) : 'parceiro(a)'}
            </p>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <span style={{ fontSize: '1.75rem', fontWeight: 800, color: scoreColor(overallScore), fontFamily: 'var(--font-display)' }}>{overallScore}</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/100</span>
          </div>
        </div>
        <div style={{ height: '6px', background: 'var(--bg-base)', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${overallScore}%`, background: scoreColor(overallScore), borderRadius: '3px', transition: 'width 1s ease' }} />
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {MAIN_TAB_KEYS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={activeTab === tab.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: '0.75rem' }}>
            {t(`pages.synastry.${tab.tKey}`)}
            {tab.key === 'analise' && aiLoading && <span style={{ marginLeft: '0.3rem', opacity: 0.7 }}>⏳</span>}
          </button>
        ))}
      </div>

      {/* ── Tab content ────────────────────────────────────── */}
      <div className="card animate-fade-in" key={activeTab} style={{ marginBottom: '1.5rem' }}>

        {activeTab === 'score' && (
          <div>
            {/* SVG arc gauge */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem', paddingTop: '0.5rem' }}>
              <div style={{ position: 'relative', display: 'inline-block', width: '160px', height: '160px' }}>
                <svg viewBox="0 0 120 120" style={{ width: '160px', height: '160px' }}>
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" />
                  <circle cx="60" cy="60" r="50" fill="none"
                    stroke={scoreColor(overallScore)} strokeWidth="7"
                    strokeDasharray={`${(overallScore / 100) * 314} 314`}
                    strokeLinecap="round"
                    transform="rotate(-90 60 60)"
                    style={{ transition: 'stroke-dasharray 1.2s ease' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: 900, color: scoreColor(overallScore), fontFamily: 'var(--font-display)', lineHeight: 1 }}>{overallScore}</span>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>/ 100</span>
                </div>
              </div>
              <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0.75rem 0 0.15rem' }}>
                Seu Vínculo Cósmico
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                {RELATION_LABELS[relationType] ?? (overallScore >= 75 ? 'Alta compatibilidade' : overallScore >= 55 ? 'Boa compatibilidade' : 'Relação desafiadora')}
              </p>
            </div>
            {(Object.keys(SCORE_CONFIG) as Array<keyof CompatibilityScores>).map(k => (
              <ScoreLine key={k} cfg={SCORE_CONFIG[k]} value={synData.scores[k]} />
            ))}
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: '1.25rem 0' }} />
            <h4 style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.75rem', fontWeight: 600 }}>Elementos</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
              {(['fire','earth','air','water'] as const).map(el => {
                const labels = { fire: '🔥 Fogo', earth: '🌍 Terra', air: '💨 Ar', water: '💧 Água' };
                return (
                  <div key={el} style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', fontSize: 'var(--text-caption)' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>{labels[el]}</p>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{userName}: {synData.elementsA[el]} | {partnerName}: {synData.elementsB[el]}</p>
                  </div>
                );
              })}
            </div>
            <h4 style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.75rem', fontWeight: 600 }}>Modalidades</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
              {(['cardinal','fixed','mutable'] as const).map(mod => {
                const labels = { cardinal: '⚡ Cardinal', fixed: '🏔️ Fixo', mutable: '🌊 Mutável' };
                return (
                  <div key={mod} style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', fontSize: 'var(--text-caption)' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>{labels[mod]}</p>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{synData.modalitiesA[mod]} / {synData.modalitiesB[mod]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'aspectos' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>Aspectos Inter-Carta ({synData.aspects.length})</h3>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {ASPECT_FILTERS.map(([k, l]) => (
                <button key={k} onClick={() => setAspectFilter(k)}
                  className={aspectFilter === k ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                  style={{ fontSize: '0.7rem' }}>{l}</button>
              ))}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-caption)' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)' }}>
                    <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontWeight: 500 }}>{userName}</th>
                    <th style={{ textAlign: 'center', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Aspecto</th>
                    <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontWeight: 500 }}>{partnerName}</th>
                    <th style={{ textAlign: 'center', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Orbe</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAspects.slice(0, 30).map((a, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-default)' }}>
                      <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-primary)' }}>{a.planet1}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                        <span style={{ color: aspectColor(a.quality), fontWeight: 600 }}>{a.aspectPt}</span>
                      </td>
                      <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-primary)' }}>{a.planet2}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>{a.orb.toFixed(1)}°</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAspects.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem' }}>Nenhum aspecto para este filtro.</p>}
            </div>
          </div>
        )}

        {activeTab === 'casas' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>Casas Ativadas</h3>
            {([
              { label: `Planetas de ${partnerName} no mapa de ${userName}`, houses: synData.housesActivatedInA },
              { label: `Planetas de ${userName} no mapa de ${partnerName}`, houses: synData.housesActivatedInB },
            ] as const).map(({ label, houses }) => (
              <div key={label} style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.5rem' }}>{label}</p>
                {houses.filter(h => h.planets.length > 0 || [1,4,5,7,8,10].includes(h.house)).map(h => (
                  <div key={h.house} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-default)' }}>
                    <span>
                      <span style={{ fontWeight: 600, color: h.planets.length > 0 ? 'var(--petrol)' : 'var(--text-muted)', fontSize: 'var(--text-label)' }}>Casa {h.house}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginLeft: '0.5rem' }}>{h.theme}</span>
                    </span>
                    <span style={{ fontSize: 'var(--text-caption)', color: h.planets.length > 0 ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                      {h.planets.length > 0 ? h.planets.join(', ') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'composto' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Mapa Composto (Ponto Médio)</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>Síntese astrológica da relação em si.</p>
            <PlanetTable planets={synData.composite.planets} />
          </div>
        )}

        {activeTab === 'davison' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Mapa de Davison</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>
              Data Davison: <strong style={{ color: 'var(--petrol)' }}>{synData.davisonDate.toLocaleDateString('pt-BR')}</strong>
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>Mapa real da relação calculado pela data média dos dois nascimentos.</p>
            <PlanetTable planets={synData.davison.planets} />
          </div>
        )}

        {activeTab === 'transitos' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Trânsitos ao Mapa Composto</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
              Céu de hoje ({new Date().toLocaleDateString('pt-BR')}) em aspecto com o mapa composto.
            </p>
            {transitsToComposite.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-caption)' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)' }}>
                    <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Trânsito</th>
                    <th style={{ textAlign: 'center', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Aspecto</th>
                    <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Composto</th>
                    <th style={{ textAlign: 'center', padding: '0.3rem 0.5rem', fontWeight: 500 }}>Orbe</th>
                  </tr>
                </thead>
                <tbody>
                  {transitsToComposite.slice(0, 15).map((a, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-default)' }}>
                      <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-primary)' }}>{a.planet1}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}><span style={{ color: aspectColor(a.quality), fontWeight: 600 }}>{a.aspectPt}</span></td>
                      <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-primary)' }}>{a.planet2}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>{a.orb.toFixed(1)}°</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>Nenhum trânsito significativo no momento.</p>}
          </div>
        )}

        {activeTab === 'progressoes' && (
          <div>
            <h3 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Progressões Secundárias</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1.25rem' }}>Regra 1 dia = 1 ano. Sol, Lua e Vênus progredidos para {new Date().getFullYear()}.</p>
            {[{ name: userName, prog: synData.progressionsA }, { name: partnerName, prog: synData.progressionsB }].map(({ name, prog }) => (
              <div key={name} style={{ marginBottom: '1.25rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.5rem' }}>{name}</p>
                {[
                  { label: '☀️ Sol Progredido',    p: prog.sun },
                  { label: '🌙 Lua Progredida',    p: prog.moon },
                  { label: '♀ Vênus Progredida', p: prog.venus },
                ].map(({ label, p }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', marginBottom: '0.4rem' }}>
                    <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontSize: 'var(--text-label)', color: 'var(--petrol)', fontWeight: 600 }}>{p.sign} {p.degree.toFixed(1)}°</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'analise' && (
          <div>
            {/* streaming live */}
            {aiLoading && streamingText && (
              <div className="reading-text" style={{ marginBottom: '1rem' }}
                dangerouslySetInnerHTML={{ __html: renderMd(streamingText) }} />
            )}
            {aiLoading && !streamingText && (
              <PhaseLoader phases={SYNASTRY_LOADING_PHASES} />
            )}
            {aiError && !aiLoading && (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)' }}>
                {aiError}
              </div>
            )}
            {/* completed: show sub-section tabs */}
            {doneText && !aiLoading && analysisSections.length > 0 && (
              <div>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {analysisSections.map((sec, idx) => (
                    <button key={idx} onClick={() => setAiSubTab(idx)}
                      className={aiSubTab === idx ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                      style={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: '0.7rem' }}>
                      {sec.num}. {sec.title}
                    </button>
                  ))}
                </div>
                <div className="reading-text"
                  dangerouslySetInnerHTML={{ __html: renderMd(analysisSections[aiSubTab]?.content ?? '') }} />
                <ReadingActions pageName="Sinastria" reading={doneText} signEmoji="💫" />
              </div>
            )}
            {/* completed but no parsed sections, show raw text */}
            {doneText && !aiLoading && analysisSections.length === 0 && (
              <div>
                <div className="reading-text" dangerouslySetInnerHTML={{ __html: renderMd(doneText) }} />
                <ReadingActions pageName="Sinastria" reading={doneText} signEmoji="💫" />
              </div>
            )}
          </div>
        )}

      </div>{/* end card */}

      {/* Cross-sell: Perguntar aos Astros */}
      <div style={{ border: '1px solid rgba(192,132,252,0.3)', background: 'rgba(192,132,252,0.06)', borderRadius: 'var(--radius-sm)', padding: '1.25rem', marginTop: '1rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-small)', marginBottom: '0.5rem' }}>
          A análise trouxe alguma dúvida específica sobre essa conexão? Consulte os astros diretamente.
        </p>
        <Link to="/questions" style={{ color: '#c084fc', fontWeight: 600, fontSize: 'var(--text-small)', textDecoration: 'none' }}>
          Perguntar aos Astros →
        </Link>
      </div>

      <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={onNewAnalysis}>
        {t('pages.synastry.newAnalysis')}
      </button>

    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SYNASTRY_LOADING_PHASES = [
  'Comparando os dois mapas natais...',
  'Calculando aspectos entre os planetas de vocês...',
  'Analisando pontos de harmonia e tensão...',
  'Preparando o relatório de compatibilidade...',
];

export function SynastryPage() {
  useSystemUsageTracker('sinastria');
  const { profile }                   = useAuth();
  const { credits, isUnlimited, refresh, creditsError } = useCredits();
  const navigate                      = useNavigate();
  const { t, lang }                   = useI18n();
  const { getProduct }                = usePricing();
  const avulsoProduct                 = useAvulsoProduct('sinastria');

  // Form
  const [partnerName,      setPartnerName]      = useState('');
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [partnerBirthTime, setPartnerBirthTime] = useState('');
  const [partnerCity,      setPartnerCity]      = useState('');
  const [partnerSign,      setPartnerSign]      = useState('');
  const [relationType,     setRelationType]     = useState<'amigo'|'crush'|'parceiro'|'parente'|'trabalho'|''>('');

  // Results
  const [synData,       setSynData]       = useState<FullSynastryData | null>(null);
  const [streamingText, setStreamingText] = useState('');
  const [doneText,      setDoneText]      = useState('');
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiError,       setAiError]       = useState<string | null>(null);
  const [calcError,     setCalcError]     = useState<string | null>(null);

  // UI
  const [activeTab,     setActiveTab]     = useState('score');
  const [aspectFilter,  setAspectFilter]  = useState('all');
  const [aiSubTab,      setAiSubTab]      = useState(0);
  const [inviteLink,    setInviteLink]    = useState('');
  const [history,       setHistory]       = useState<HistoryItem[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showForm,      setShowForm]      = useState(false);
  const [showAvulsoModal, setShowAvulsoModal] = useState(false);

  // Derived
  const partnerDate   = useMemo(() => displayToDate(partnerBirthDate), [partnerBirthDate]);
  const autoSign      = useMemo(() => partnerDate ? signFromDate(partnerDate) : '', [partnerDate]);
  const effectiveSign = partnerDate ? autoSign : partnerSign;
  const canGenerate   = !aiLoading && (isUnlimited || credits.synastry > 0) && !!partnerName && !!relationType && partnerDate !== null;
  const analysisText = doneText || streamingText;

  // Parsed streaming sections: split on **N. Title** markers, strip --- dividers
  const analysisSections = useMemo(() => {
    if (!analysisText) return [];
    const parts = analysisText.split(/(?=\*\*\d+\.\s)/);
    return parts.map(part => {
      const m = part.match(/^\*\*(\d+)\.\s(.+?)\*\*/);
      if (!m) return null;
      const content = part
        .replace(/^\*\*\d+\.\s.+?\*\*\n?/, '')
        .replace(/^---\s*\n?|---\s*$/g, '')
        .trim();
      return { num: +m[1], title: m[2], content };
    }).filter(Boolean) as { num: number; title: string; content: string }[];
  }, [analysisText]);

  // Transits to composite (client-side, no API)
  const transitsToComposite = useMemo(() => {
    if (!synData) return [];
    return calcInterAspects(synData.currentSky, synData.composite.planets);
  }, [synData]);

  const filteredAspects = useMemo(() => {
    if (!synData) return [];
    return aspectFilter === 'all'
      ? synData.aspects
      : synData.aspects.filter(a => a.aspect === aspectFilter);
  }, [synData, aspectFilter]);

  // Deduplicated history, one card per unique partner (most recent first)
  const uniqueHistory = useMemo(() => {
    const seen = new Set<string>();
    return history.filter(h => {
      const key = `${h.partner_name}|${h.partner_birth_date}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [history]);

  useEffect(() => {
    if (doneText || streamingText) { setDoneText(''); setStreamingText(''); setSynData(null); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load history
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('synastry_readings')
      .select('id, partner_name, partner_birth_date, compatibility_score, relation_type, reading_text, created_at')
      .eq('user_id', profile.id)
      .not('reading_text', 'is', null)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (data) setHistory(data as HistoryItem[]);
        setHistoryLoaded(true);
      });
  }, [profile?.id, doneText]);

  async function generate() {
    if (!canGenerate || !profile?.birth_date || partnerDate === null) return;
    setCalcError(null);
    setAiError(null);
    setStreamingText('');
    setDoneText('');
    setSynData(null);
    setAiSubTab(0);

    // Phase 1: client-side calculations (synchronous, instant)
    let data: FullSynastryData;
    try {
      const userDate = new Date(profile.birth_date + 'T12:00:00');
      data = calculateFullSynastry(userDate, partnerDate);
      setSynData(data);
      setActiveTab('score');
    } catch (err) {
      setCalcError(String(err));
      return;
    }

    // Phase 2: check cache first (no credit consumed for repeat partner)
    const isAdmin = profile?.role === 'admin';
    if (profile?.id && !isAdmin) {
      const { data: cached } = await supabase
        .from('synastry_readings')
        .select('reading_text')
        .eq('user_id', profile.id)
        .eq('partner_name', partnerName)
        .eq('partner_birth_date', displayToDb(partnerBirthDate))
        .not('reading_text', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cached?.reading_text) {
        setDoneText(cached.reading_text);
        setActiveTab('analise');
        return;
      }
    }

    // Phase 2b: streaming AI analysis
    setAiLoading(true);
    const overallScore = Math.round(
      Object.values(data.scores).reduce((a, b) => a + b, 0) / 7,
    );
    const top10 = data.aspects.slice(0, 10).map(a =>
      `${a.planet1} ${a.aspectPt} ${a.planet2} (${a.orb.toFixed(1)}°, ${a.quality === 'harmonious' ? 'harmônico' : a.quality === 'tense' ? 'tenso' : 'neutro'})`
    ).join('\n');

    streamInvoke('synastry-stream', {
      body: {
        partnerName,
        partnerBirthDate: displayToDb(partnerBirthDate),
        partnerBirthTime: partnerBirthTime  || undefined,
        partnerSunSign:   effectiveSign     || undefined,
        relationType:     relationType      || undefined,
        calcContext: {
          overallScore,
          top10Aspects:  top10,
          elementsA:     data.elementsA,
          elementsB:     data.elementsB,
          modalitiesA:   data.modalitiesA,
          modalitiesB:   data.modalitiesB,
          progressionsA: data.progressionsA,
          progressionsB: data.progressionsB,
          compositeSun:   data.composite.planets.find(p => p.key === 'Sun'),
          compositeMoon:  data.composite.planets.find(p => p.key === 'Moon'),
          compositeVenus: data.composite.planets.find(p => p.key === 'Venus'),
          davisonDate:    data.davisonDate.toISOString().split('T')[0],
        },
      },
      timeout: 120_000,
      onChunk(acc) { setStreamingText(acc); },
      onComplete(full) {
        setDoneText(full);
        setStreamingText('');
        setAiLoading(false);
        refresh();
        // Save from frontend as reliable fallback, edge function INSERT
        // runs in a non-awaited Promise that Deno may discard before resolving.
        if (profile?.id && full) {
          supabase.from('synastry_readings').insert({
            user_id:             profile.id,
            partner_name:        partnerName,
            partner_birth_date:  displayToDb(partnerBirthDate),
            partner_birth_time:  partnerBirthTime || null,
            partner_sun_sign:    effectiveSign    || null,
            relation_type:       relationType     || null,
            reading_text:        full,
            compatibility_score: overallScore,
            status:              'completed',
          }).then(({ error }) => {
            if (error) console.error('[synastry] frontend save error:', error);
          });
        }
      },
      onError(msg) {
        setAiError(msg === 'insufficient_credits' ? t('pages.synastry.noCreditsMsg') : t('pages.synastry.errorMsg'));
        setAiLoading(false);
      },
    });
  }

  function loadFromHistory(item: HistoryItem) {
    if (!item.reading_text) return;
    const [y, m, d] = (item.partner_birth_date ?? '').split('-');
    setPartnerName(item.partner_name ?? '');
    setPartnerBirthDate(y && m && d ? `${d}/${m}/${y}` : '');
    setPartnerSign('');
    setRelationType((item.relation_type as typeof relationType) || '');
    setShowForm(false);
    const userDate = profile?.birth_date ? new Date(profile.birth_date + 'T12:00:00') : null;
    const pDate    = y && m && d ? new Date(+y, +m - 1, +d) : null;
    if (userDate && pDate) {
      try { setSynData(calculateFullSynastry(userDate, pDate)); } catch { /* ignore */ }
    }
    setDoneText(item.reading_text);
    setActiveTab('analise');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deletePartner(item: HistoryItem) {
    await supabase
      .from('synastry_readings')
      .delete()
      .eq('user_id', profile!.id)
      .eq('partner_name', item.partner_name);
    setHistory(prev => prev.filter(h => h.partner_name !== item.partner_name));
  }

  async function generateInviteLink() {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('synastry_invite_tokens')
      .insert({ user_id: profile.id })
      .select('token')
      .single();
    if (data?.token) {
      const link = `${window.location.origin}/invite/${data.token}`;
      setInviteLink(link);
      window.open(`https://wa.me/?text=${encodeURIComponent(`Faça nossa sinastria no Professya ✦\n${link}`)}`, '_blank');
    }
  }

  if (creditsError) return (
    <ToolPageShell icon={Heart} title={t('pages.synastry.title')} subtitle={t('pages.synastry.subtitle')}>
      <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Erro ao carregar créditos.</p>
        <button className="btn btn-secondary btn-sm" onClick={refresh}>Tentar novamente</button>
      </div>
    </ToolPageShell>
  );
  if (!profile?.birth_date) return (
    <ToolPageShell icon={Heart} title={t('pages.synastry.title')} subtitle={t('pages.synastry.subtitle')}>
      <EmptyState emoji="💫" title={t('pages.synastry.emptyTitle')}
        subtitle={t('pages.synastry.emptySubtitle')} cta={t('pages.synastry.emptyCta')}
        ctaAction={() => navigate('/profile')} />
    </ToolPageShell>
  );

  const noCredits  = !isUnlimited && credits.synastry <= 0;
  const userName   = profile.full_name?.split(' ')[0] ?? 'Você';
  const overallScore = synData ? Math.round(Object.values(synData.scores).reduce((a, b) => a + b, 0) / 7) : 0;

  return (
    <ToolPageShell
      icon={Heart}
      title={t('pages.synastry.title')}
      subtitle={t('pages.synastry.subtitle')}
      headerRight={!synData && historyLoaded && uniqueHistory.length > 0 && !showForm ? (
        <button className="btn btn-primary btn-sm" style={{ flexShrink: 0, whiteSpace: 'nowrap', borderRadius: '999px', padding: '0.5rem 1.125rem' }}
          onClick={() => setShowForm(true)}>
          + Adicionar Parceiro
        </button>
      ) : undefined}
    >
      {/* Credit status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', marginTop: '-0.5rem' }}>
        <span className="credit-pill">💫 {isUnlimited ? '∞' : credits.synastry} crédito{!isUnlimited && credits.synastry === 1 ? '' : 's'}</span>
        {noCredits && <button className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>Comprar ✦</button>}
      </div>

      {/* ── Pre-results view ───────────────────────────────── */}
      {!synData && (
        <>
          {/* ── Suas Conexões card (list) ───────────────────── */}
          {historyLoaded && uniqueHistory.length > 0 && !showForm && (
            <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem' }}>
              <h2 className="font-display" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                Suas Conexões
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {uniqueHistory.map((item, idx) => {
                  const sc  = item.compatibility_score ?? 0;
                  const initial = (item.partner_name ?? '?')[0].toUpperCase();
                  const badge = item.relation_type ? RELATION_BADGE_LABELS[item.relation_type] : null;
                  return (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.875rem',
                      padding: '0.875rem 0',
                      borderTop: idx > 0 ? '1px solid var(--border-default)' : 'none',
                      cursor: 'pointer',
                    }}
                      onClick={() => loadFromHistory(item)}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '2.5rem', height: '2.5rem', borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(31,78,95,0.2)', border: '1px solid rgba(31,78,95,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem', fontWeight: 700, color: 'var(--petrol)',
                      }}>{initial}</div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)' }}>
                          {item.partner_name}
                        </p>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {badge && (
                            <span style={{
                              fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.5rem',
                              borderRadius: '999px', background: 'rgba(255,255,255,0.08)',
                              color: 'var(--text-secondary)', border: '1px solid var(--border-default)',
                            }}>{badge}</span>
                          )}
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor(sc) }}>{sc}%</span>
                        </div>
                      </div>
                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                        <span style={{ padding: '0.35rem', color: 'var(--text-muted)' }}>
                          <Eye size={18} />
                        </span>
                        <button title="Excluir" onClick={e => { e.stopPropagation(); deletePartner(item); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.35rem', color: '#f87171' }}>
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Show form when requested or first use ───────── */}
          {showForm && historyLoaded && uniqueHistory.length > 0 && (
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1rem' }}
              onClick={() => setShowForm(false)}>
              ← Minhas conexões
            </button>
          )}

          {/* ── New partner form ─────────────────────────────── */}
          {(showForm || !historyLoaded || uniqueHistory.length === 0) && <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1.25rem' }}>
              Dados do parceiro
            </h2>

            {/* Name */}
            <div className="form-group">
              <label className="form-label" htmlFor="pname">Nome *</label>
              <input id="pname" type="text" className="form-input" value={partnerName}
                onChange={e => setPartnerName(e.target.value)} placeholder="Nome da pessoa" />
            </div>

            {/* Relation type */}
            <div className="form-group">
              <label className="form-label">Tipo de relação *</label>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {([
                  { key: 'amigo',    label: '👥 Amigo(a)' },
                  { key: 'crush',    label: '💝 Crush' },
                  { key: 'parceiro', label: '❤️ Parceiro(a)' },
                  { key: 'parente',  label: '👨‍👩‍👧 Parente' },
                  { key: 'trabalho', label: '💼 Trabalho' },
                ] as const).map(({ key, label }) => (
                  <button key={key} type="button"
                    onClick={() => setRelationType(key)}
                    className={relationType === key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                    style={{ fontSize: '0.8rem' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Birth date */}
            <div className="form-group">
              <label className="form-label" htmlFor="pdate">
                Data de nascimento *
              </label>
              <input id="pdate" type="text" className="form-input" value={partnerBirthDate}
                onChange={e => setPartnerBirthDate(e.target.value)} placeholder="DD/MM/AAAA" maxLength={10} />
            </div>

            {/* Solar sign, disabled & auto-filled when date is set */}
            <div className="form-group">
              <label className="form-label" htmlFor="psign">
                Signo solar
                {partnerDate && (
                  <span style={{ color: 'var(--petrol)', fontWeight: 400, fontSize: 'var(--text-caption)', marginLeft: '0.35rem' }}>
                    (detectado automaticamente)
                  </span>
                )}
              </label>
              <select id="psign" className="form-select"
                value={partnerDate ? autoSign : partnerSign}
                disabled={!!partnerDate}
                onChange={e => setPartnerSign(e.target.value)}
                style={partnerDate ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                <option value="">Selecione o signo</option>
                {ZODIAC_SIGNS.map(s => <option key={s.key} value={s.key}>{s.emoji} {s.name}</option>)}
              </select>
            </div>

            {/* Birth time (optional) */}
            <div className="form-group">
              <label className="form-label" htmlFor="ptime">
                Hora de nascimento <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 'var(--text-caption)' }}>(opcional)</span>
              </label>
              <input id="ptime" type="text" className="form-input" value={partnerBirthTime}
                onChange={e => setPartnerBirthTime(e.target.value)} placeholder="HH:MM" maxLength={5} />
            </div>

            {/* Birth city (optional) */}
            <div className="form-group">
              <label className="form-label" htmlFor="pcity">
                Local de nascimento <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 'var(--text-caption)' }}>(opcional)</span>
              </label>
              <input id="pcity" type="text" className="form-input" value={partnerCity}
                onChange={e => setPartnerCity(e.target.value)} placeholder="Ex: São Paulo, SP" />
            </div>

            {/* WhatsApp invite (collapsed) */}
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Share2 size={13} /> Prefere que a pessoa preencha os dados dela?
              </summary>
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(31,78,95,0.04)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-sm)' }}>
                {inviteLink ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <code style={{ flex: 1, fontSize: 'var(--text-caption)', backgroundColor: 'var(--bg-base)', padding: '0.4rem 0.6rem', borderRadius: 'var(--radius-sm)', color: 'var(--petrol)', wordBreak: 'break-all' }}>{inviteLink}</code>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard.writeText(inviteLink)}>Copiar</button>
                    <button className="btn btn-secondary btn-sm" style={{ backgroundColor: '#25D366', borderColor: '#25D366', color: '#fff' }}
                      onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Faça nossa sinastria no Professya ✦\n${inviteLink}`)}`, '_blank')}>
                      WhatsApp
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={generateInviteLink} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Share2 size={14} /> Gerar link de convite via WhatsApp
                  </button>
                )}
              </div>
            </details>

            {/* Submit */}
            {canGenerate ? (
              <button className="btn btn-primary btn-block" onClick={generate}>
                Analisar Compatibilidade ✦
              </button>
            ) : (
              <button className="btn btn-primary btn-block" disabled style={{ opacity: 0.45 }}>
                Analisar Compatibilidade ✦
              </button>
            )}
            {!relationType && partnerName && (
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textAlign: 'center', marginTop: '0.5rem' }}>
                Selecione o tipo de relação para continuar.
              </p>
            )}
            {relationType && partnerName && !partnerDate && !effectiveSign && (
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textAlign: 'center', marginTop: '0.5rem' }}>
                Informe a data de nascimento ou o signo solar.
              </p>
            )}
            {noCredits && (
              <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '0.4rem' }}>
                  {t('pages.synastry.noCreditsMsg')}{' '}
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/wallet')}>{t('pages.synastry.buyLink')}</button>
                </p>
                {avulsoProduct && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAvulsoModal(true)} style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', borderColor: 'rgba(31,78,95,0.4)' }}>
                    Comprar análise avulsa por R${avulsoProduct.price_brl.toFixed(2).replace('.', ',')} · Ver comparativo
                  </button>
                )}
              </div>
            )}
          </div>}
        </>
      )}

      {/* ── Calc error ─────────────────────────────────────── */}
      {calcError && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {calcError}
        </div>
      )}

      {/* ── Results (synData is set instantly) ─────────────── */}
      {synData && (
        <SynastryResults
          synData={synData}
          userName={userName}
          partnerName={partnerName}
          partnerSign={partnerSign}
          relationType={relationType}
          overallScore={overallScore}
          profile={profile}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          aspectFilter={aspectFilter}
          setAspectFilter={setAspectFilter}
          filteredAspects={filteredAspects}
          transitsToComposite={transitsToComposite}
          aiLoading={aiLoading}
          aiError={aiError}
          streamingText={streamingText}
          doneText={doneText}
          analysisSections={analysisSections}
          aiSubTab={aiSubTab}
          setAiSubTab={setAiSubTab}
          onNewAnalysis={() => {
            setSynData(null); setDoneText(''); setStreamingText('');
            setPartnerName(''); setPartnerBirthDate(''); setPartnerBirthTime('');
            setPartnerCity(''); setPartnerSign(''); setRelationType('');
            setAspectFilter('all'); setShowForm(false);
          }}
        />
      )}
      {showAvulsoModal && avulsoProduct && (
        <AvulsoCompareModal
          serviceName="Sinastria"
          serviceSlug="sinastria"
          avulsoPrice={avulsoProduct.price_brl}
          avulsoDesc={avulsoProduct.description}
          monthlyProd={getProduct('monthly_sub')}
          annualProd={getProduct('annual_sub')}
          onClose={() => setShowAvulsoModal(false)}
        />
      )}

    </ToolPageShell>
  );
}
